import React, { useEffect } from "react";
import { Modal, Form, Input, Button, notification, DatePicker } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateDrawing } from "../services/drawingsAPI";
import { useAuth } from "../hooks/useAuth";
import dayjs from "dayjs";
import { DATE_PICKER_FORMAT } from "../constants/dateConstants";

const UpdateDrawingModal = ({ visible, onClose, drawing }) => {
  const { selectedProject } = useAuth();
  const projectId = selectedProject?.project_id;
  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  useEffect(() => {
    if (drawing) {
      form.setFieldsValue({
        title: drawing.title,
        drawing_number: drawing.drawing_number,
        revision_date: dayjs(drawing.revision_date),
        revision_title: drawing.revision_title,
      });
    }
  }, [drawing, form]);

  const mutation = useMutation({
    mutationFn: (data) => updateDrawing(projectId, drawing._id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["drawings", projectId]);
      onClose();
      notification.success({ message: "Drawing updated successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update drawing",
        description: error.message,
      });
    },
  });

  const handleUpdateDrawing = (values) => {
    const drawingData = {
      ...values,
      revision_date: values.revision_date.format("YYYY-MM-DD"),
    };
    mutation.mutate(drawingData);
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal
      title="Update Drawing"
      open={visible}
      onCancel={handleClose}
      footer={null}
    >
      <Form form={form} onFinish={handleUpdateDrawing} layout="vertical">
        <Form.Item
          label="Title"
          name="title"
          rules={[
            { required: true, message: "Please input the drawing title!" },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Drawing Number"
          name="drawing_number"
          rules={[
            { required: true, message: "Please input the drawing number!" },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Revision Date"
          name="revision_date"
          rules={[
            { required: true, message: "Please select the revision date!" },
          ]}
        >
          <DatePicker style={{ width: "100%" }} format={DATE_PICKER_FORMAT} />
        </Form.Item>

        <Form.Item
          label="Revision Title"
          name="revision_title"
          rules={[
            { required: true, message: "Please input the revision title!" },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={mutation.isPending}
            style={{ width: "100%" }}
          >
            Update Drawing
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default UpdateDrawingModal;
